import { EventStreamCodec } from '@smithy/eventstream-codec';
import { fromUtf8, toUtf8 } from '@smithy/util-utf8';

/** Amazon Transcribe's streaming websocket takes PCM at this rate, mono
 *  (CAPTURE_JOBS.md section 3). */
export const TRANSCRIBE_SAMPLE_RATE = 16_000;

const PCM_PROCESSOR_NAME = 'aica-pcm-capture-processor';

/**
 * Runs inside the AudioWorklet's own global scope, so it cannot import
 * anything: it is loaded as a Blob module by `createPcmAudioSource`
 * rather than bundled as a file of its own. Each 128-sample render quantum
 * is posted to the main thread as a `Float32Array`; `floatTo16BitPcm`
 * turns it into the bytes Transcribe wants.
 */
const PCM_PROCESSOR_SOURCE = `
class AicaPcmCaptureProcessor extends AudioWorkletProcessor {
  process(inputs) {
    const channel = inputs[0]?.[0];
    if (channel && channel.length > 0) {
      this.port.postMessage(channel.slice());
    }
    return true;
  }
}
registerProcessor('${PCM_PROCESSOR_NAME}', AicaPcmCaptureProcessor);
`;

const codec = new EventStreamCodec(toUtf8, fromUtf8);

/** One render quantum of mic audio, float32 in [-1, 1], as little-endian
 *  16-bit PCM. */
export function floatTo16BitPcm(input: Float32Array): Uint8Array {
  const bytes = new Uint8Array(input.length * 2);
  const view = new DataView(bytes.buffer);
  for (let i = 0; i < input.length; i += 1) {
    const clamped = Math.max(-1, Math.min(1, input[i]));
    view.setInt16(i * 2, clamped < 0 ? clamped * 0x8000 : clamped * 0x7fff, true);
  }
  return bytes;
}

/** Wraps one chunk of PCM bytes in the `AudioEvent` frame Transcribe's
 *  streaming websocket expects. An empty chunk is the documented
 *  end-of-stream signal. */
export function encodeAudioEvent(pcm: Uint8Array): Uint8Array {
  return codec.encode({
    headers: {
      ':message-type': { type: 'string', value: 'event' },
      ':event-type': { type: 'string', value: 'AudioEvent' },
      ':content-type': { type: 'string', value: 'application/octet-stream' },
    },
    body: pcm,
  });
}

export interface LiveTranscriptChunk {
  text: string;
  /** False once Transcribe has settled on this chunk's wording. */
  isPartial: boolean;
}

export type IncomingMessage =
  | { kind: 'chunk'; chunk: LiveTranscriptChunk }
  | { kind: 'error'; message: string }
  | { kind: 'ignored' };

/**
 * Decodes one AWS event-stream frame from the Transcribe streaming
 * websocket. Pure: no socket, no AudioContext, so `stream.test.ts` drives
 * it directly by round-tripping `codec.encode`.
 */
export function parseIncomingMessage(bytes: Uint8Array): IncomingMessage {
  let decoded;
  try {
    decoded = codec.decode(bytes);
  } catch {
    return { kind: 'ignored' };
  }

  const messageType = decoded.headers[':message-type']?.value;
  const bodyText = toUtf8(decoded.body);

  if (messageType === 'exception') {
    let message = 'Live transcription reported an error.';
    try {
      const parsed = JSON.parse(bodyText) as { Message?: string };
      if (parsed.Message) message = parsed.Message;
    } catch {
      // Keep the generic message: the exception frame carried no JSON body.
    }
    return { kind: 'error', message };
  }

  const eventType = decoded.headers[':event-type']?.value;
  if (eventType !== 'TranscriptEvent') return { kind: 'ignored' };

  try {
    const parsed = JSON.parse(bodyText) as {
      Transcript?: { Results?: Array<{ IsPartial?: boolean; Alternatives?: Array<{ Transcript?: string }> }> };
    };
    const result = parsed.Transcript?.Results?.[0];
    const text = result?.Alternatives?.[0]?.Transcript ?? '';
    if (!result || !text) return { kind: 'ignored' };
    return { kind: 'chunk', chunk: { text, isPartial: Boolean(result.IsPartial) } };
  } catch {
    return { kind: 'ignored' };
  }
}

export interface AudioSourceHandle {
  /** Tears down the worklet, the audio graph and the AudioContext.
   *  Idempotent; never touches the `MediaStream` itself, which the caller
   *  owns (`lib/voice/microphone.ts`). */
  stop: () => void;
}

/** Injectable in `startLiveTranscription`, and replaced wholesale in
 *  tests: jsdom implements neither `AudioContext` nor `AudioWorklet`. */
export type CreateAudioSource = (
  stream: MediaStream,
  onChunk: (pcm: Uint8Array) => void,
  onError: (message: string) => void
) => AudioSourceHandle;

/**
 * Captures the microphone as 16 kHz mono PCM via an `AudioWorklet`
 * (CAPTURE_JOBS.md section 3), posting each render quantum back as
 * little-endian 16-bit samples. The worklet module is loaded from a Blob
 * URL rather than a separate static file, so this stays one file with no
 * bundler-specific worklet wiring.
 */
export function createPcmAudioSource(
  stream: MediaStream,
  onChunk: (pcm: Uint8Array) => void,
  onError: (message: string) => void
): AudioSourceHandle {
  const AudioContextCtor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!AudioContextCtor) {
    onError('Live voice needs a browser with Web Audio support.');
    return { stop: () => {} };
  }

  const context = new AudioContextCtor({ sampleRate: TRANSCRIBE_SAMPLE_RATE });
  let source: MediaStreamAudioSourceNode | null = null;
  let node: AudioWorkletNode | null = null;
  let sink: GainNode | null = null;
  let cancelled = false;

  void (async () => {
    try {
      const blobUrl = URL.createObjectURL(new Blob([PCM_PROCESSOR_SOURCE], { type: 'application/javascript' }));
      try {
        await context.audioWorklet.addModule(blobUrl);
      } finally {
        URL.revokeObjectURL(blobUrl);
      }
      if (cancelled) return;

      source = context.createMediaStreamSource(stream);
      node = new AudioWorkletNode(context, PCM_PROCESSOR_NAME);
      node.port.onmessage = (event: MessageEvent<Float32Array>) => {
        onChunk(floatTo16BitPcm(event.data));
      };
      // The graph only runs while it has a path to the destination; a
      // silent gain keeps the worklet pumping without echoing the
      // operator's own voice back at them.
      sink = context.createGain();
      sink.gain.value = 0;
      source.connect(node);
      node.connect(sink);
      sink.connect(context.destination);
    } catch {
      onError('Could not start live transcription audio.');
    }
  })();

  return {
    stop: () => {
      cancelled = true;
      node?.disconnect();
      source?.disconnect();
      sink?.disconnect();
      void context.close().catch(() => {});
    },
  };
}

/** The subset of `WebSocket` this module touches, so tests inject a fake
 *  one with no real network. */
export interface MinimalSocket {
  readyState: number;
  binaryType?: string;
  send: (data: ArrayBufferLike | ArrayBufferView) => void;
  close: (code?: number, reason?: string) => void;
  onopen: ((event: Event) => void) | null;
  onmessage: ((event: MessageEvent) => void) | null;
  onerror: ((event: Event) => void) | null;
  onclose: ((event: CloseEvent) => void) | null;
}

const SOCKET_OPEN = 1;

export interface StartLiveTranscriptionOptions {
  /** The presigned wss URL from `startTranscribeSession`, valid 5 minutes. */
  url: string;
  stream: MediaStream;
  onChunk: (chunk: LiveTranscriptChunk) => void;
  /** Mic denial surfaces separately (`requestMicrophoneStream`); this is
   *  every socket- and audio-pipeline failure after that. */
  onError: (message: string) => void;
  createSocket?: (url: string) => MinimalSocket;
  createAudioSource?: CreateAudioSource;
}

export interface LiveTranscriptionSession {
  /** Sends the end-of-stream frame, closes the socket and tears down the
   *  audio pipeline. Idempotent. */
  stop: () => void;
}

/**
 * Streams the microphone to Amazon Transcribe over the presigned
 * websocket (CAPTURE_JOBS.md section 3) and reports partial then final
 * results through `onChunk`. `LiveTranscriptBox` owns the resulting text;
 * this module only moves bytes.
 */
export function startLiveTranscription(options: StartLiveTranscriptionOptions): LiveTranscriptionSession {
  const {
    url,
    stream,
    onChunk,
    onError,
    createSocket = (socketUrl) => new WebSocket(socketUrl) as unknown as MinimalSocket,
    createAudioSource = createPcmAudioSource,
  } = options;

  let stopped = false;
  let audioSource: AudioSourceHandle | null = null;
  const socket = createSocket(url);
  socket.binaryType = 'arraybuffer';

  socket.onopen = () => {
    if (stopped) return;
    audioSource = createAudioSource(
      stream,
      (pcm) => {
        if (stopped || socket.readyState !== SOCKET_OPEN) return;
        socket.send(encodeAudioEvent(pcm));
      },
      onError
    );
  };

  socket.onmessage = (event) => {
    const data = event.data;
    const bytes = data instanceof ArrayBuffer ? new Uint8Array(data) : null;
    if (!bytes) return;
    const parsed = parseIncomingMessage(bytes);
    if (parsed.kind === 'chunk') onChunk(parsed.chunk);
    else if (parsed.kind === 'error') onError(parsed.message);
  };

  socket.onerror = () => {
    onError('The live transcription connection failed.');
  };

  socket.onclose = (event) => {
    if (!stopped && event.code !== 1000) {
      onError('The live transcription connection closed unexpectedly.');
    }
  };

  function stop(): void {
    if (stopped) return;
    stopped = true;
    audioSource?.stop();
    audioSource = null;
    if (socket.readyState === SOCKET_OPEN) {
      try {
        socket.send(encodeAudioEvent(new Uint8Array(0)));
      } catch {
        // The empty frame is a courtesy end-of-stream signal, not required
        // for a clean local stop.
      }
    }
    socket.close(1000);
  }

  return { stop };
}
